import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ReviewsDataService, ReviewsColumnsService } from '../../services';
import { DialogReviewFormComponent } from './review-form/review-form.component';
import { DialogReviewDetailsComponent } from './review-details/review-details.component';
import { Review, ReviewsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ReviewsComponent implements OnInit, OnDestroy {

  private reviewAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private reviewEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private reviewDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public reviewToDelete: Review = {};
  public reviews: Review[] = [];
  public review: Review = {};
  public selectedReviews: Review[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private reviewsDataService: ReviewsDataService,
    private reviewsColumnsService: ReviewsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ReviewsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Reviews - Management');
    this.primengConfig.ripple = true;
    this.getReviews();
  }

  ngOnDestroy(): void {
    console.log('ReviewsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getReviews(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.reviewsDataService.getReviews(this.parameters).subscribe((response: ReviewsResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.reviews = response.results;
        } else {
          this.reviews = this.reviews.concat(response.results);
        }
        if (response.inlineCount > this.reviews.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getReviews();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('REVIEWSNOTLOADED') });
    });
  }

  importReviews(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.reviewsDataService.addReviews(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('REVIEWSCREATED') });
                if (proceed === j) {
                  this.getReviews();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('REVIEWSNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('REVIEWSNOTCREATED') + ' ' + msg });
            });
          } else {
            this.reviewsDataService.updateReviews(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('REVIEWSUPDATED') });
                if (proceed === j) {
                  this.getReviews();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('REVIEWSNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('REVIEWSNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.reviewsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('reviews.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedReviews() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Reviews?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.reviewsDataService.deleteReviews(this.selectedReviews).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Reviews Deleted', life: 3000 });
            this.getReviews();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Reviews not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Reviews not deleted' });
        });
        this.selectedReviews = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.reviews, this.columns, 'Reviews');
  }

  exportExcel() {
    ToolsService.exportExcel(this.reviews, this.columns, 'Reviews');
  }

  exportCsv() {
    ToolsService.exportCsv(this.reviews, this.columns, 'Reviews');
  }

  refresh() {
    this.reviewsDataService.clearCache();
    this.getReviews();
  }

  reviewAdd(): void {
    if (!this.navigation.getParamValue('reviews.addInDialog', true)) {
      this.router.navigate(['/reviews/add']);
      return;
    }
    this.reviewAddDialog = this.dialogService.open(DialogReviewFormComponent, {
      header: '', // this.translate.instant('NEWREVIEW'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        review: { id: null },
        users: this.reviewsColumnsService.users,
      },
    });

    this.reviewAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Review added' });
        this.getReviews();
      }
    });
  }

  reviewEdit(review: Review): void {
    if (!this.navigation.getParamValue('reviews.editInDialog', true)) {
      this.router.navigate(['/reviews/edit/' + review.id]);
      return;
    }
    this.reviewEditDialog = this.dialogService.open(DialogReviewFormComponent, {
      header: '', // this.translate.instant('EDITREVIEW') + ' ' + review.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        review: review,
        users: this.reviewsColumnsService.users,
      },
    });

    this.reviewEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Review updated' });
        this.getReviews();
      }
    });
  }

  reviewDetails(review: Review): void {
    if (!this.navigation.getParamValue('reviews.detailsInDialog', true)) {
      this.router.navigate(['/reviews/details/' + review.id]);
      return;
    }
    this.reviewDetailsDialog = this.dialogService.open(DialogReviewDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        review: review,
      }
    });
  }

  reviewDelete(review: Review): void {
    this.reviewToDelete = review;
    this.messageService.clear();
    this.messageService.add({
      key: 'reviewDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + review.entity + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.reviewsDataService.deleteReview(this.reviewToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Review deleted' });
      this.getReviews();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Review not deleted' });
    });
  }

  onDeleteReject(): void {
    this.reviewToDelete = {};
    this.messageService.clear('reviewDelete');
  }

}
